import React from "react";
import { Link } from "react-router-dom";
import { Bed, Building2, Compass, Maximize2 } from "lucide-react";
import { useI18n } from "@/lib/i18n";
import { BACKEND_URL } from "@/lib/api";

const toFullUrl = (src) => src?.startsWith("/api/") ? `${BACKEND_URL}${src}` : src;

const formatINR = (paise) => "₹" + Math.round((paise || 0) / 100).toLocaleString("en-IN");

function getCardPrice(plan) {
  const files = plan.files || [];
  // Headline price = Floor Plan; otherwise the cheapest paid file; null = nothing priced.
  const fp = files.find(f => f.file_type === "floor_plan" && (f.price_paise || 0) > 0);
  if (fp) return { paise: fp.price_paise, isStartingFrom: false };
  const paid = files.filter(f => (f.price_paise || 0) > 0).sort((a, b) => a.price_paise - b.price_paise);
  if (paid.length) return { paise: paid[0].price_paise, isStartingFrom: true };
  return null;
}

export default function PlanCard({ plan, testid }) {
  const { t } = useI18n();
  const price = getCardPrice(plan);
  return (
    <Link
      to={`/plans/${plan.slug || plan.id}`}
      className="nc-card overflow-hidden group block"
      data-testid={testid || `plan-card-${plan.id}`}
    >
      <div className="aspect-[4/3] overflow-hidden bg-slate-100">
        <img
          src={toFullUrl(plan.thumbnail) || "https://images.pexels.com/photos/4458210/pexels-photo-4458210.jpeg"}
          alt={plan.title}
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
          loading="lazy"
        />
      </div>
      <div className="p-4">
        <div className="flex items-center justify-between gap-2 mb-2">
          <div className="flex items-center gap-2 min-w-0">
            <span className="nc-chip">{plan.length}×{plan.width} ft</span>
            {plan.is_featured && <span className="nc-chip nc-chip-blue">{t("planCard.featured")}</span>}
          </div>
          {price && (
            <div className="text-right flex-shrink-0" data-testid={`plan-card-price-${plan.id}`}>
              {price.isStartingFrom && <div className="text-[10px] text-slate-400 leading-none">{t("planCard.from")}</div>}
              <div className="text-base font-bold text-slate-900 leading-tight">{formatINR(price.paise)}</div>
              <div className="text-[10px] text-slate-400 leading-none">{t("planCard.gst")}</div>
            </div>
          )}
        </div>
        <h3 className="font-semibold text-slate-900 mb-2 line-clamp-1 text-base">{plan.title}</h3>
        <div className="grid grid-cols-2 gap-y-1.5 text-xs text-slate-500">
          <span className="flex items-center gap-1.5"><Bed className="w-3.5 h-3.5" /> {plan.bhk} {t("plan.bhk")}</span>
          <span className="flex items-center gap-1.5"><Building2 className="w-3.5 h-3.5" /> {plan.floors} {t("plan.floors")}</span>
          <span className="flex items-center gap-1.5"><Compass className="w-3.5 h-3.5" /> {t(`facing.${plan.facing}`) || plan.facing}</span>
          <span className="flex items-center gap-1.5"><Maximize2 className="w-3.5 h-3.5" /> {plan.area} sqft</span>
        </div>
      </div>
    </Link>
  );
}
